/**
 * Reconciliation — the tie-out shown as arithmetic, not a verdict badge.
 *
 * Three outcomes, and UNVERIFIED is a first-class one: when the provider's balance is unavailable there is
 * no delta, and we never print a zero into that gap. It renders hatched and dashed — shape, not colour.
 *
 * The trend answers the question a single day cannot: a one-day break is an incident, a growing delta is a
 * systemic fault.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { api, type ReconBreak, type ReconRun } from "../lib/api";
import { useLoader, useSession } from "../lib/session";
import { dateTimeLong, money, relative } from "../lib/format";
import { ActionButton, C, card, cardPad, CardHead, Drawer, EmptyState, ErrorState, input, label, labelText, Skeleton, Tick, Warn } from "../ui/primitives";

interface Summary { ledgerCollection?: number; providerTotal?: number | null; delta?: number | null }

export function Reconciliation() {
  const runs = useLoader(() => api.reconRuns(), []);
  const [showResolved, setShowResolved] = useState(false);
  const breaks = useLoader(() => api.reconBreaks(showResolved ? "resolved" : undefined), [showResolved]);
  const [resolving, setResolving] = useState<ReconBreak | null>(null);

  if (runs.loading) return <div style={cardPad}><Skeleton rows={3} label="Loading the tie-out…" /></div>;
  if (runs.error) return <div style={card}><ErrorState title="Couldn't load reconciliation." body={runs.error} onRetry={runs.reload} /></div>;

  const all: ReconRun[] = runs.data ?? [];
  const latest = all[0] ?? null;
  const s: Summary = (latest?.summary ?? {}) as Summary;
  const unverified = latest ? s.providerTotal === null || s.providerTotal === undefined : false;
  const delta = s.delta ?? null;

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(330px,1fr))", gap: 12 }}>
        <div style={cardPad}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>
              {latest ? `Latest tie-out — ${latest.providerKey} collection account` : "No tie-out has run yet"}
            </div>
            <div style={{ fontSize: 11.5, color: "var(--muted)", fontWeight: 600, marginTop: 1 }}>
              {latest
                ? `Run ${dateTimeLong(latest.ranAt, { tz: true })} · ${relative(latest.ranAt)}. Arithmetic, not a verdict badge.`
                : "Reconciliation runs daily once a provider is configured."}
            </div>
          </div>

          {latest && (
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              <div style={row}>
                <span style={rowLabel}>Our ledger says</span>
                <span className="bp-mono" style={{ fontSize: 14, fontWeight: 700 }}>{money(s.ledgerCollection ?? 0)}</span>
              </div>

              {unverified ? (
                <div style={{ ...row, background: "repeating-linear-gradient(45deg,var(--subtle),var(--subtle) 8px,var(--surface) 8px,var(--surface) 16px)", border: "1px dashed var(--muted2)" }}>
                  <span style={rowLabel}>{latest.providerKey} says</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: C.warn }}>NO ANSWER — balance endpoint unavailable</span>
                </div>
              ) : (
                <div style={row}>
                  <span style={rowLabel}>{latest.providerKey} says</span>
                  <span className="bp-mono" style={{ fontSize: 14, fontWeight: 700 }}>{money(s.providerTotal ?? 0)}</span>
                </div>
              )}

              {unverified ? (
                <div style={{ padding: "12px 13px", border: "1px dashed var(--muted2)", borderRadius: 11, display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: C.warn }}>UNVERIFIED — not passed, not broken.</span>
                  <span style={{ fontSize: 12, lineHeight: 1.5, color: "var(--ink2)" }}>
                    The provider balance was unavailable, so no delta exists. We never print a zero into this gap. The float stays unverified until a run succeeds.
                  </span>
                </div>
              ) : delta === 0 ? (
                <div style={{ display: "flex", gap: 9, alignItems: "center", padding: "10px 13px" }}>
                  <Tick />
                  <span style={{ fontSize: 13, fontWeight: 800, color: C.good }}>Delta <span className="bp-mono">{money(0)}</span> — reconciled to the kobo.</span>
                </div>
              ) : (
                <div style={{ padding: "12px 13px", border: `1px solid ${C.dangerLine}`, background: C.dangerSoft, borderRadius: 11, display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: C.danger }}>Delta <span className="bp-mono" style={{ fontSize: 16 }}>{money(delta ?? 0)}</span></span>
                  <span style={{ fontSize: 12, lineHeight: 1.5, color: "var(--ink2)" }}>
                    {(delta ?? 0) > 0
                      ? <><b>Positive:</b> money at the provider we have not recorded. Look for a missed webhook.</>
                      : <><b>Negative:</b> our ledger believes in money the provider does not have. Look for a collection the provider later failed or reversed without telling us.</>}
                  </span>
                </div>
              )}
            </div>
          )}

          <div style={{ fontSize: 11.5, color: "var(--muted)", fontWeight: 600, lineHeight: 1.5 }}>
            Positive delta = money at the provider we haven't recorded. Negative = we believe in money the provider doesn't have.
          </div>
        </div>

        <Trend runs={all} />
      </div>

      <div style={card}>
        <CardHead title="Breaks"
          sub="Worked, never deleted: open → investigating → resolved. Resolved breaks stay in history with their written reason."
          right={
            <button type="button" onClick={() => setShowResolved(!showResolved)} style={{ padding: "8px 11px", border: "1px solid var(--line2)", borderRadius: 10, background: "var(--surface)", color: "var(--ink2)", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
              {showResolved ? "Show open" : "Show resolved"}
            </button>
          } />
        {breaks.loading ? <Skeleton rows={2} label="Loading breaks…" />
          : breaks.error ? <ErrorState title="Couldn't load breaks." body={breaks.error} onRetry={breaks.reload} />
          : (breaks.data ?? []).length === 0 ? (
            <EmptyState tone={showResolved ? "neutral" : "good"}
              title={showResolved ? "No resolved breaks yet." : "No open breaks."}
              body={showResolved ? "Nothing has been worked and closed in this window." : "Every tie-out that has run agreed with the provider. This is the system working — not a broken page."} />
          ) : (
            (breaks.data ?? []).map((b) => <BreakRow key={b.id} b={b} onResolve={() => setResolving(b)} />)
          )}
      </div>

      {resolving && (
        <ResolveDrawer b={resolving} onClose={() => setResolving(null)} onDone={() => { setResolving(null); breaks.reload(); runs.reload(); }} />
      )}
    </>
  );
}

function Trend({ runs }: { runs: ReconRun[] }) {
  // Oldest → newest, last 7 runs. A run with no delta (unverified) is drawn as a gap, never as zero.
  const series = [...runs].slice(0, 7).reverse();
  const deltas: (number | null)[] = series.map((r) => (r.summary as Summary).delta ?? null);
  const max = Math.max(1, ...deltas.map((d) => (d === null ? 0 : Math.abs(d))));
  // "Worsening" = every run at least as bad as the one before, and the last strictly worse than the first.
  // A single bad day is an incident; only a monotone climb earns the systemic warning.
  const known = deltas.filter((d): d is number => d !== null);
  const worsening = known.length >= 3
    && Math.abs(known[known.length - 1] ?? 0) > Math.abs(known[0] ?? 0)
    && known.every((d, i) => i === 0 || Math.abs(d) >= Math.abs(known[i - 1] ?? 0));

  return (
    <div style={cardPad}>
      <div>
        <div style={{ fontWeight: 800, fontSize: 15 }}>Delta, recent runs</div>
        <div style={{ fontSize: 11.5, color: "var(--muted)", fontWeight: 600, marginTop: 1 }}>A single day's break is an incident. A growing delta is a systemic fault.</div>
      </div>
      {series.length === 0 ? (
        <EmptyState title="No history yet." body="The trend appears once reconciliation has run more than once." />
      ) : (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", height: 110, padding: "0 4px" }}>
          {series.map((r, i) => {
            // `?? null` collapses the index read's `undefined` into the one "no answer" case, so a run with
            // no provider figure and a run past the end of the array are handled identically — as a gap.
            const d = deltas[i] ?? null;
            const h = d === null ? 0 : Math.max(8, Math.round((Math.abs(d) / max) * 62));
            return (
              <div key={r.id} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 5, height: "100%", justifyContent: "flex-end" }}>
                <span className="bp-mono" style={{ fontSize: 9.5, fontWeight: 700, color: "var(--ink2)", whiteSpace: "nowrap" }}>
                  {d === null ? "?" : d === 0 ? "0" : `${d < 0 ? "−" : ""}${(Math.abs(d) / 1000).toFixed(1)}k`}
                </span>
                {d === null
                  ? <div title="unverified — no provider answer" style={{ width: "100%", maxWidth: 34, height: 26, border: "1px dashed var(--muted2)", borderRadius: 4, background: "repeating-linear-gradient(45deg,transparent,transparent 4px,var(--subtle) 4px,var(--subtle) 8px)" }} />
                  : d === 0
                    ? <div style={{ width: "100%", maxWidth: 34, height: 3, background: C.good, borderRadius: 2 }} />
                    : <div style={{ width: "100%", maxWidth: 34, height: h, background: `repeating-linear-gradient(45deg,${C.danger},${C.danger} 4px,#D9705F 4px,#D9705F 8px)`, borderRadius: "4px 4px 2px 2px" }} />}
                <span style={{ fontSize: 9.5, fontWeight: 700, color: "var(--muted)" }}>{new Date(r.ranAt).getDate()}</span>
              </div>
            );
          })}
        </div>
      )}
      {worsening && (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "10px 12px", border: `1px solid ${C.dangerLine}`, background: C.dangerSoft, borderRadius: 10 }}>
          <Warn size={14} />
          <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.5, color: "var(--ink2)" }}>
            Growing run over run. Treat as systemic — something is leaking on every day's postings, not a one-off missed event.
          </span>
        </div>
      )}
    </div>
  );
}

function BreakRow({ b, onResolve }: { b: ReconBreak; onResolve: () => void }) {
  const { locked } = useSession();
  const resolved = b.state === "resolved";
  const amount = b.amount === null ? null : Number(b.amount);
  return (
    <div style={{ padding: "13px 16px", borderBottom: "1px solid var(--line)", display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span className="bp-mono" style={{ fontSize: 12.5, fontWeight: 800 }}>{b.id.slice(0, 8)}</span>
        <span style={resolved
          ? { fontSize: 10, fontWeight: 800, letterSpacing: ".05em", color: C.good, border: `1.5px solid ${C.good}`, borderRadius: 6, padding: "2px 7px" }
          : { fontSize: 10, fontWeight: 800, letterSpacing: ".05em", color: "#fff", background: b.state === "investigating" ? C.warn : C.danger, borderRadius: 6, padding: "3px 8px" }}>
          {b.state.toUpperCase()}
        </span>
        {amount !== null && <span className="bp-mono" style={{ fontSize: 13, fontWeight: 800, color: amount < 0 ? C.danger : "var(--ink)" }}>{money(amount, { signed: true })}</span>}
        <span style={{ fontSize: 11.5, color: "var(--muted)", fontWeight: 600, marginLeft: "auto" }}>
          raised {dateTimeLong(b.createdAt, { tz: true })} · {relative(b.createdAt)}
        </span>
      </div>
      <div style={{ fontSize: 12.5, color: "var(--ink2)", lineHeight: 1.5 }}>{b.description}</div>
      {b.resolution && (
        <div style={{ padding: "9px 12px", background: "var(--subtle)", border: "1px solid var(--line)", borderRadius: 10, fontSize: 12, lineHeight: 1.5, color: "var(--ink2)" }}>
          <b>Resolution{b.resolvedAt ? ` — ${dateTimeLong(b.resolvedAt)}` : ""}:</b> {b.resolution}
        </div>
      )}
      {!resolved && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Link to="/movements" style={{ padding: "8px 12px", border: "1px solid var(--line2)", borderRadius: 10, fontSize: 12, fontWeight: 800 }}>See the movements behind this →</Link>
          <ActionButton small locked={locked} onClick={onResolve}>Resolve with a written reason</ActionButton>
        </div>
      )}
    </div>
  );
}

function ResolveDrawer({ b, onClose, onDone }: { b: ReconBreak; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const amount = b.amount === null ? null : Number(b.amount);

  async function save() {
    if (reason.trim().length < 3) { setErr("A reason is required — write it for the next person."); return; }
    setBusy(true); setErr(null);
    try { await api.resolveBreak(b.id, reason.trim()); onDone(); }
    catch (e) { setErr(e instanceof Error ? e.message : "Could not resolve"); setBusy(false); }
  }

  return (
    <Drawer title={`Resolve break ${b.id.slice(0, 8)}`} onClose={onClose} width={430}>
      <div style={{ padding: "11px 13px", border: `1px solid ${C.warnLine}`, background: C.warnSoft, borderRadius: 11, fontSize: 12.5, lineHeight: 1.55, color: "var(--ink2)" }}>
        <b>This marks {amount === null ? "this break" : `a ${money(Math.abs(amount))} break`} as resolved. It does not move any money</b> — if money must move, post the correcting journal first and reference it below. The break, your reason and your name stay in history permanently.
      </div>
      <label style={label}>
        <span style={labelText}>Reason — required, written for the next person</span>
        <textarea rows={5} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="What was wrong, how you know, and what fixed it"
          style={{ ...input, lineHeight: 1.5, resize: "vertical" }} />
      </label>
      {err && <div style={{ fontSize: 12, fontWeight: 700, color: C.danger }}>{err}</div>}
      <div style={{ display: "flex", gap: 9 }}>
        <ActionButton tone="quiet" onClick={onClose}>Cancel</ActionButton>
        <div style={{ flex: 1 }}><button type="button" onClick={() => void save()} disabled={busy}
          style={{ width: "100%", padding: 12, border: "none", borderRadius: 11, background: busy ? "var(--muted)" : C.accent, color: "#fff", fontSize: 12.5, fontWeight: 800, cursor: busy ? "wait" : "pointer" }}>
          {busy ? "Recording…" : "Resolve break — recorded under my name"}
        </button></div>
      </div>
    </Drawer>
  );
}

const row: React.CSSProperties = { display: "flex", justifyContent: "space-between", gap: 10, padding: "11px 13px", background: "var(--subtle)", border: "1px solid var(--line)", borderRadius: 11 };
const rowLabel: React.CSSProperties = { fontSize: 12.5, fontWeight: 700, color: "var(--ink2)", textTransform: "capitalize" };
